import React from "react";
import {
  Box,
  Stack,
  Typography,
  Divider,
  Paper,
  useTheme,
} from "@mui/material";

// ─── Helpers ────────────────────────────────────────────────────────────────
const RUPEE_SYMBOL = "₹";
const formatAmount = (num) =>
  Number(num || 0)
    .toFixed(0) // round like Kotlin’s roundToInt()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// ─── Main Component ─────────────────────────────────────────────────────────
const PaymentDetails = ({ order = {} }) => {
  const theme = useTheme();

  /* ------------------------------------------------------------
     Derive amounts exactly like the Kotlin example
  ------------------------------------------------------------- */
  const amount = ((order.price ?? 0) * (order.quantity_required ?? 0)) / 100;

  const shAmount =
    order.shipping_required === "Yes" ? order.shipping_charge ?? 0 : 0;

  const bbAmount =
    order.blackbox_required === "Yes" ? order.blackbox_charge ?? 0 : 0;

  const totalAmount = amount + shAmount + bbAmount;

  const totalReceived = order.total_paid ?? 0;
  const balanceAmount = totalAmount - totalReceived;

  /* ------------------------------------------------------------
     Reusable row element for neat layout
  ------------------------------------------------------------- */
  const Row = ({ label, value, highlight = false }) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: 0.5,
        fontWeight: highlight ? 600 : 400,
        color: highlight
          ? theme.palette.text.primary
          : theme.palette.text.secondary,
      }}
    >
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2">
        {RUPEE_SYMBOL}
        {formatAmount(value)}
      </Typography>
    </Stack>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        Payment Details
      </Typography>

      {/* ── Charges ─────────────────────────────────────────── */}
      <Stack spacing={0.5}>
        <Row label="Amount" value={amount} />
        <Row label="Shipping Charge" value={shAmount} />
        <Row label="Black-box Charge" value={bbAmount} />
      </Stack>

      <Divider sx={{ my: 1 }} />

      {/* ── Totals ──────────────────────────────────────────── */}
      <Stack spacing={0.5}>
        <Row label="Total Payable" value={totalAmount} highlight />
        <Row label="Total Received" value={totalReceived} />
        <Row
          label="Balance Due"
          value={balanceAmount}
          highlight={balanceAmount > 0}
        />
      </Stack>
    </Paper>
  );
};

export default PaymentDetails;
